import {
  YoutubePublish,
  YoutubePublishPayload,
  AssetItem,
  MonetizationOptions,
  YoutubePlaylist,
  MediaModel,
  SocialChannel,
} from "@/database";
import BasePublishCard, { OnPublished } from "./BasePublishCard";
import { CustomSelect } from "../..";
import { PlatformConfig } from "./platformConfig";
import { DEFAULT_YOUTUBE_CATEGORIES } from "../../../constants";

interface YoutubePublishCardProps {
  initialdata: YoutubePublish;
  assets: AssetItem[];
  disabled: boolean;
  handleCancelOrDelete: (
    initialdata: YoutubePublish,
    type: string,
    cardType: string
  ) => void;
  callPublishMedia: (
    payload: YoutubePublishPayload,
    cb: (response: any) => void
  ) => void;
  updatePublishMedia: (
    payload: YoutubePublishPayload,
    cb: (response: any) => void
  ) => void;
  isPublishingMedia: boolean;
  mediaBaseUrl: string;
  expandedId: string | null;
  onToggleExpand: (id: string) => void;
  getYoutubePlaylist: any;
  youtubePlaylist: YoutubePlaylist[];
  updateModal: any;
  isWizard: boolean;
  currentMediaDetails: MediaModel;
  isPreset?: boolean;
  handleAllSelectedPresets?: (presets: any[]) => void;
  item?: any;
  presetDetails?: any;
  isPreview?: boolean;
  readOnlyMode?: boolean;
  metaPresetData?: any;
  bunchLanguageList?: any[];
  socialChannelList?: SocialChannel[];
  inSheet?: boolean;
  onPublished?: OnPublished;
  onStageDraft?: (
    id: string,
    payload: any,
    opts?: { silent?: boolean }
  ) => void;
  autoStage?: boolean;
  onFieldEdited?: (
    id: string,
    field: "title" | "desc" | "tags" | "thumbnail"
  ) => void;
  onPersistEditedValues?: (id: string, patch: Record<string, any>) => void;
  onCardSaved?: (id: string) => void;
}

const YoutubePublishCard = ({
  initialdata,
  assets,
  disabled = false,
  handleCancelOrDelete,
  callPublishMedia,
  updatePublishMedia,
  mediaBaseUrl,
  expandedId,
  onToggleExpand,
  getYoutubePlaylist,
  youtubePlaylist,
  updateModal,
  isWizard,
  currentMediaDetails,
  isPreset = false,
  handleAllSelectedPresets,
  item,
  presetDetails,
  isPreview = false,
  readOnlyMode = false,
  metaPresetData,
  bunchLanguageList,
  socialChannelList = [],
  inSheet,
  onPublished,
  onStageDraft,
  autoStage,
  onFieldEdited,
  onPersistEditedValues,
  onCardSaved,
}: YoutubePublishCardProps) => {
  // Render YouTube-specific fields
  const renderPlatformSpecificFields = (
    formik: any,
    config: PlatformConfig,
    enabledFields: Record<string, boolean>,
    handleFieldToggle: (fieldName: string, checked: boolean) => void,
    monetizationOptions?: MonetizationOptions[],
    youtubePlaylist?: YoutubePlaylist[],
    isSftp?: boolean,
    // BasePublishCard's own disabled - for a published card mid Edit ->
    // Sync -> Update, this unlocks once sync completes, unlike the outer
    // `disabled` prop above which stays locked for the whole card lifetime.
    // Governs Category/Playlist only.
    effectiveDisabled: boolean = disabled,
    // Monetize/Created-for-kids/Rights-management never unlock on an
    // already-published video, even once effectiveDisabled above does -
    // BasePublishCard passes its own lockedOnPublished (disabled ||
    // isPublishedMedia) here, same lock the slate edit/delete buttons use.
    lockedFieldsDisabled: boolean = effectiveDisabled
  ) => {
    return (
      <>
        {/* Category */}
        <div className="col-md-3">
          <CustomSelect
            value={formik.values.category ?? ""}
            label="CATEGORY"
            handleChange={(newValue: any) => {
              formik.setFieldValue("category", newValue.value);
            }}
            options={Object.entries(DEFAULT_YOUTUBE_CATEGORIES).map(
              ([key, value]) => ({
                label: value,
                value: key,
              })
            )}
            disabled={effectiveDisabled || isPreview}
            error={formik.errors.category}
            touched={formik.touched.category}
            showRequired={!isPreview}
            labelCheckbox={false}
            labelCheckboxChecked={enabledFields.category}
            onLabelCheckboxChange={(newValue: any) => {
              handleFieldToggle("category", newValue);
            }}
            disableLabelCheckbox={
              isPreview && !(formik.values.category?.length > 0)
            }
            readOnlyMode={readOnlyMode}
          />
        </div>

        {/* Monetization */}
        {isSftp && (
          <div className="col-md-3">
            <CustomSelect
              value={formik.values.monetize_policy_id || ""}
              label="MONETIZE VIDEO"
              options={
                monetizationOptions?.map((option) => ({
                  label: option.title,
                  value: option.id,
                })) || []
              }
              handleChange={(newValue: any) => {
                formik.setFieldValue("monetize_policy_id", newValue.value);
                formik.setFieldValue("monetize_policy_name", newValue.label);
              }}
              disabled={lockedFieldsDisabled || isPreview}
              error={formik.errors.monetize_policy_id}
              touched={formik.touched.monetize_policy_id}
              showRequired={!isPreview}
              labelCheckbox={false}
              labelCheckboxChecked={enabledFields.monetize_policy_id}
              onLabelCheckboxChange={(newValue: any) => {
                handleFieldToggle("monetize_policy_id", newValue);
              }}
              disableLabelCheckbox={
                isPreview && !(formik.values.monetize_policy_id?.length > 0)
              }
              readOnlyMode={readOnlyMode}
              updatedOptions={monetizationOptions?.map((option) => ({
                label: option.title,
                value: option.id,
              }))}
            />
          </div>
        )}

        {/* Playlist */}
        <div className="col-md-3">
          <CustomSelect
            value={
              formik.values.playlist
                ? formik.values.playlist.split(",").filter(Boolean)
                : []
            }
            label="ADD TO PLAYLIST"
            handleChange={(newValue: any) => {
              const selectedValues = newValue
                .map((v: any) => v.value)
                .join(",");
              formik.setFieldValue("playlist", selectedValues || "");
            }}
            options={(youtubePlaylist || []).map(
              (playlist: YoutubePlaylist) => ({
                label: playlist.title,
                value: playlist.id,
              })
            )}
            disabled={effectiveDisabled || isPreview}
            touched={formik.touched.playlist}
            error={formik.errors.playlist}
            showRequired={false}
            multiSelect={true}
            labelCheckbox={false}
            labelCheckboxChecked={enabledFields.playlist}
            onLabelCheckboxChange={(newValue: any) => {
              handleFieldToggle("playlist", newValue);
            }}
            disableLabelCheckbox={
              isPreview && !(formik.values.playlist?.length > 0)
            }
            readOnlyMode={readOnlyMode}
            updatedOptions={(youtubePlaylist || []).map(
              (playlist: YoutubePlaylist) => ({
                label: playlist.title,
                value: playlist.id,
              })
            )}
          />
        </div>

        {/* Created for Kids */}
        <div className="col-md-3">
          <CustomSelect
            value={formik.values.is_created_for_kids ?? "no"}
            label="CREATED FOR KIDS"
            options={[
              { label: "Yes", value: "yes" },
              { label: "No", value: "no" },
            ]}
            handleChange={(newValue: any) => {
              formik.setFieldValue("is_created_for_kids", newValue.value);
            }}
            disabled={lockedFieldsDisabled || isPreview}
            showRequired={!isPreview}
            labelCheckbox={false}
            touched={formik.touched.is_created_for_kids}
            error={formik.errors.is_created_for_kids}
            labelCheckboxChecked={enabledFields.is_created_for_kids}
            onLabelCheckboxChange={(newValue: any) => {
              handleFieldToggle("is_created_for_kids", newValue);
            }}
            disableLabelCheckbox={
              isPreview && !(formik.values.is_created_for_kids?.length > 0)
            }
            readOnlyMode={readOnlyMode}
          />
        </div>

        {/* Rights Management */}
        {isSftp && (
          <div className="col-md-3">
            <CustomSelect
              value={formik.values.is_rights_managements_allowed ?? "yes"}
              label="RIGHTS MANAGEMENTS"
              options={[
                { label: "Yes", value: "yes" },
                { label: "No", value: "no" },
              ]}
              handleChange={(newValue: any) => {
                formik.setFieldValue(
                  "is_rights_managements_allowed",
                  newValue.value
                );
              }}
              disabled={lockedFieldsDisabled || isPreview}
              showRequired={!isPreview}
              labelCheckbox={false}
              labelCheckboxChecked={enabledFields.is_rights_managements_allowed}
              onLabelCheckboxChange={(newValue: any) => {
                handleFieldToggle("is_rights_managements_allowed", newValue);
              }}
              disableLabelCheckbox={
                isPreview &&
                !(formik.values.is_rights_managements_allowed?.length > 0)
              }
              readOnlyMode={readOnlyMode}
              touched={formik.touched.is_rights_managements_allowed}
              error={formik.errors.is_rights_managements_allowed}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <BasePublishCard
      initialdata={initialdata}
      bunchLanguageList={bunchLanguageList}
      assets={assets}
      disabled={disabled}
      handleCancelOrDelete={handleCancelOrDelete}
      callPublishMedia={callPublishMedia}
      updatePublishMedia={updatePublishMedia}
      isPublishingMedia={false}
      mediaBaseUrl={mediaBaseUrl}
      expandedId={expandedId}
      onToggleExpand={onToggleExpand}
      getYoutubePlaylist={getYoutubePlaylist}
      youtubePlaylist={youtubePlaylist}
      updateModal={updateModal}
      isWizard={isWizard}
      currentMediaDetails={currentMediaDetails}
      isPreset={isPreset}
      handleAllSelectedPresets={handleAllSelectedPresets}
      item={item}
      presetDetails={presetDetails}
      isPreview={isPreview}
      readOnlyMode={readOnlyMode}
      metaPresetData={metaPresetData}
      platform="youtube"
      inSheet={inSheet}
      onPublished={onPublished}
      onStageDraft={onStageDraft}
      autoStage={autoStage}
      onFieldEdited={onFieldEdited}
      onPersistEditedValues={onPersistEditedValues}
      onCardSaved={onCardSaved}
      renderPlatformSpecificFields={renderPlatformSpecificFields}
      socialChannelList={socialChannelList as SocialChannel[]}
    />
  );
};

export default YoutubePublishCard;
